import React, {
    CSSProperties,
    ReactElement,
    useCallback,
    useMemo,
} from "react";
import Image from "next/image";
import styles from "./styles.module.scss";
import { TFeatureList } from "@page-builder/ts/generated";
import { replacePlaceholders } from "@lib/sharedUtils";
import { useDomainContext } from "@hooks/useDomainContext";
import FeatureListItem, { FeatureItem, ListItem } from "./featureListItem";
import {
    getPublishedDateLabel,
    getPostReadingTime,
} from "@page-builder/utils/index";

const DEFAULT_ORDER = [
    "image",
    "title",
    "description",
    "note",
    "list",
    "button",
];

const DEFAULT_GRID_COLUMNS = {
    sm: 1,
    md: 2,
    lg: 3,
    xl: 3,
};

export default function FeatureList({
    props = {},
}: {
    props?: TFeatureList["props"];
}): ReactElement {
    const { visitDetails, pageBuilderBlogPost } = useDomainContext();
    const {
        title,
        description,
        items,
        order = DEFAULT_ORDER,
        gridColumns = DEFAULT_GRID_COLUMNS,
        itemLayout = "vertical",
        showCounter = false,
        buttonLayout = "vertical",
    } = props;

    const isHorizontalButtons = buttonLayout === "horizontal";

    const postUpdatedDate = pageBuilderBlogPost
        ? getPublishedDateLabel(pageBuilderBlogPost)
        : undefined;

    const readingTime = pageBuilderBlogPost
        ? getPostReadingTime(pageBuilderBlogPost)
        : undefined;

    const basePlaceholders = useMemo(
        () => ({
            region: visitDetails.region,
            city: visitDetails?.city,
        }),
        [visitDetails.region, visitDetails?.city],
    );

    const postPlaceholders = useMemo(
        () => ({
            ...basePlaceholders,
            postUpdatedDate,
            readingTime,
        }),
        [basePlaceholders, postUpdatedDate, readingTime],
    );

    const gridStyle = useMemo(
        () =>
            ({
                "--grid-cols-sm": gridColumns["sm"] || "1",
                "--grid-cols-md": gridColumns["md"] || gridColumns["sm"] || "2",
                "--grid-cols-lg":
                    gridColumns["lg"] ||
                    gridColumns["md"] ||
                    gridColumns["sm"] ||
                    "3",
                "--grid-cols-xl":
                    gridColumns["xl"] ||
                    gridColumns["lg"] ||
                    gridColumns["md"] ||
                    gridColumns["sm"] ||
                    "3",
            } as CSSProperties),
        [
            gridColumns["sm"],
            gridColumns["md"],
            gridColumns["lg"],
            gridColumns["xl"],
        ],
    );

    const renderListItems = useCallback(
        (list: ListItem[]) => {
            return list.map((listItem, listIndex) => {
                const isObject = typeof listItem === "object";
                const text: string = isObject ? listItem.text : listItem;
                const icon: string | undefined = isObject
                    ? listItem.icon
                    : undefined;
                const itemKey = `${text}-${icon ?? "no-icon"}-${listIndex}`;

                return (
                    <li key={itemKey} className={styles["listItem"]}>
                        {icon && (
                            <div className={styles["listIcon"]}>
                                <Image src={icon} layout="fill" alt="" />
                            </div>
                        )}
                        <span className={styles["listText"]}>
                            {replacePlaceholders(text ?? "", basePlaceholders)}
                        </span>
                    </li>
                );
            });
        },
        [basePlaceholders],
    );

    const renderAction = useCallback((item: FeatureItem) => {
        const buttonData = item.button;
        if (!buttonData?.text && !buttonData?.icon) return null;

        const content = (
            <>
                {buttonData.showIcon && buttonData.icon && (
                    <div className={styles["buttonIcon"]}>
                        <Image src={buttonData.icon} layout="fill" alt="" />
                    </div>
                )}
                <span>{buttonData.text}</span>
            </>
        );

        const buttonClassName = `${styles["item-button"]} ${
            buttonData.showIcon ? styles["button-with-icon"] : ""
        }`;

        if (!buttonData.download) {
            if (!buttonData.href) {
                return <span className={buttonClassName}>{content}</span>;
            }

            return (
                <a
                    href={buttonData.href}
                    className={`${buttonClassName} ${styles["button-link"]}`}
                    target={buttonData.target || "_blank"}
                    rel="noopener noreferrer"
                >
                    {content}
                </a>
            );
        }

        const handleDownload = () => {
            if (!buttonData.href) return;

            const filename = buttonData.pdfFileName
                ? buttonData.pdfFileName
                : buttonData.href?.split("/")?.pop() || "download";

            const downloadUrl = `/api/download?url=${encodeURIComponent(
                buttonData.href,
            )}&filename=${encodeURIComponent(filename)}`;
            window.location.href = downloadUrl;
        };

        return (
            <button
                type="button"
                className={buttonClassName}
                onClick={handleDownload}
            >
                {content}
            </button>
        );
    }, []);

    return (
        <section className={styles.mainSection}>
            <div>
                {title && (
                    <div
                        className={`mb-6 ${styles.title}`}
                        dangerouslySetInnerHTML={{
                            __html: replacePlaceholders(
                                title,
                                basePlaceholders,
                            ),
                        }}
                    />
                )}
                {description && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: replacePlaceholders(
                                description,
                                basePlaceholders,
                            ),
                        }}
                        className={styles.description}
                    />
                )}
                <div
                    style={gridStyle}
                    className={`grid gap-10 lg:gap-20 mt-6 ${styles["grid"]} itemsContainer`}
                >
                    {items?.map((item, index) => {
                        const featureKey =
                            item.href?.trim() ||
                            item.title?.trim() ||
                            `feature-item-${index}`;

                        return (
                            <FeatureListItem
                                key={featureKey}
                                item={item}
                                index={index}
                                order={order}
                                itemLayout={itemLayout}
                                isHorizontalButtons={isHorizontalButtons}
                                showCounter={showCounter}
                                basePlaceholders={basePlaceholders}
                                postPlaceholders={postPlaceholders}
                                renderListItems={renderListItems}
                                renderAction={renderAction}
                            />
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
